import { checkEmail, setPassword } from '@api/auth-api';
import FieldError from '@components/field-error';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  type CheckEmailFormData,
  checkEmailSchema,
  type SetPasswordFormData,
  setPasswordSchema,
} from '@validation/schemas';
import { Award } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { sileo } from 'sileo';

type Step = 'email' | 'password';

export default function CreateAccount(): React.JSX.Element {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const emailForm = useForm<CheckEmailFormData>({
    resolver: zodResolver(checkEmailSchema),
    defaultValues: { email: '' },
  });

  const passwordForm = useForm<SetPasswordFormData>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { password: '', confirmPassword: '' },
    mode: 'onChange',
  });

  const onCheckEmail = async (data: CheckEmailFormData): Promise<void> => {
    setIsLoading(true);

    try {
      const result = await checkEmail(data.email);

      if (!result.exists) {
        emailForm.setError('email', { message: 'No account found with this email' });
        return;
      }

      if (!result.needsPassword) {
        emailForm.setError('email', {
          message: 'This account already has a password. Please log in.',
        });
        return;
      }

      setEmail(data.email);
      setStep('password');
    } catch {
      sileo.error({ title: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  const onSetPassword = async (data: SetPasswordFormData): Promise<void> => {
    setIsLoading(true);

    try {
      await setPassword(email, data.password);
      sileo.success({ title: 'Password set successfully. Please log in.' });
      navigate('/', { replace: true });
    } catch {
      sileo.error({ title: 'An error occurred. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-card__accent" />
        <div className="login-card__body">
          <div className="login-card__header">
            <div className="login-card__logo">
              <Award size={28} />
            </div>
            <h1 className="login-card__title">Create Account</h1>
            <p className="login-card__description">
              {step === 'email'
                ? 'Enter your email to set up your password'
                : `Setting password for ${email}`}
            </p>
          </div>

          {step === 'email' && (
            <form
              className="login-form"
              onSubmit={(e) => void emailForm.handleSubmit(onCheckEmail)(e)}
            >
              <div className="form-group">
                <label htmlFor="email" className="form-label">
                  Email
                </label>
                <input
                  id="email"
                  type="email"
                  className={`form-input${emailForm.formState.errors.email ? ' form-input--error' : ''}`}
                  placeholder="you@example.com"
                  autoComplete="email"
                  {...emailForm.register('email')}
                />
                <FieldError error={emailForm.formState.errors.email} />
              </div>

              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? 'Checking...' : 'Continue'}
              </button>
            </form>
          )}

          {step === 'password' && (
            <form
              className="login-form"
              onSubmit={(e) => void passwordForm.handleSubmit(onSetPassword)(e)}
            >
              <div className="form-group">
                <label htmlFor="new-password" className="form-label">
                  Password
                </label>
                <input
                  id="new-password"
                  type="password"
                  className={`form-input${passwordForm.formState.errors.password ? ' form-input--error' : ''}`}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  {...passwordForm.register('password')}
                />
                <FieldError error={passwordForm.formState.errors.password} />
                {!passwordForm.formState.errors.password && (
                  <span className="form-hint">
                    Minimum 8 characters, 1 uppercase, 1 number, 1 special character
                  </span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="confirm-password" className="form-label">
                  Confirm Password
                </label>
                <input
                  id="confirm-password"
                  type="password"
                  className={`form-input${passwordForm.formState.errors.confirmPassword ? ' form-input--error' : ''}`}
                  placeholder="••••••••"
                  autoComplete="new-password"
                  {...passwordForm.register('confirmPassword')}
                />
                <FieldError error={passwordForm.formState.errors.confirmPassword} />
              </div>

              <button type="submit" className="btn btn-primary" disabled={isLoading}>
                {isLoading ? 'Setting password...' : 'Set Password'}
              </button>
            </form>
          )}

          <div style={{ textAlign: 'center', marginTop: 'var(--space-5)' }}>
            <Link to="/" className="btn btn-ghost btn-sm">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
